
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Row, Table } from '@themesberg/react-bootstrap';
import { useCookies } from "react-cookie";
import topbar from "topbar";
import { getCurrentGamePeriods, getCurrentPeriodTicket } from "../../../controller/api/gamePeriod";
import { updateEstimateJackpotValue } from "../../../controller/api/game";
import { disableTicketRoll, enableTicketRoll } from "../../../controller/api/systemApi";
import { LinkWeb } from "./partials/LinkWeb";
import { ButtonVLText } from "./partials/ButtonVLText";
import { MuaChungToggle } from "./partials/MuaChungToggle";
import ModuleMaintainPanel from "../../../components/ModuleMaintainPanel";
import TicketSourceSummary from "../../statistic/ticket-source-summary/TicketSourceSummary";

export default () => {
  const [cookies] = useCookies(['lottmekong']);

  const [data, setData] = useState({})
  const [periodRows, setPeriodRows] = useState([])
  const [jackpotDraftByGame, setJackpotDraftByGame] = useState({})
  const [updatingJackpotGameId, setUpdatingJackpotGameId] = useState('')

  const [loaded, setLoaded] = useState(true);

  const getAllFunc = useCallback(async () => {
    try {
      setLoaded(false)
      const [ticketResp, periodResp] = await Promise.all([
        getCurrentPeriodTicket(cookies.lottmekong),
        getCurrentGamePeriods(cookies.lottmekong),
      ])

      setData(ticketResp)
      setPeriodRows(periodResp)
      setJackpotDraftByGame(
        (periodResp || []).reduce((acc, row) => {
          acc[row.gameId] = {
            jackpot1: row.jackpot1 || '',
            jackpot2: row.jackpot2 || '',
          }
          return acc
        }, {})
      )
    } finally {
      setLoaded(true)
    }
  }, [cookies.lottmekong])

  useEffect(() => {
    if (loaded) {
      topbar.hide();
    } else {
      topbar.show();
    }
  }, [loaded])

  useEffect(() => {
    document.title = "Trang chủ"
  }, []);

  useEffect(() => {
    topbar.config({
      autoRun: true,
      barThickness: 3,
      barColors: {
        '0': 'rgba(26,  188, 156, .9)',
        '.25': 'rgba(52,  152, 219, .9)',
        '.50': 'rgba(241, 196, 15,  .9)',
        '.75': 'rgba(230, 126, 34,  .9)',
        '1.0': 'rgba(211, 84,  0,   .9)'
      },
      shadowBlur: 10,
      shadowColor: 'rgba(0,   0,   0,   .6)'
    })
    getAllFunc();
    return () => {
    }
  }, [getAllFunc])

  const onEnableRollClick = async (gameId, isGroup = false) => {
    await enableTicketRoll(cookies.lottmekong, gameId, false, false, isGroup)
    getAllFunc()
  }

  const onDisableRollClick = async (gameId, isGroup = false) => {
    await disableTicketRoll(cookies.lottmekong, gameId, false, false, isGroup)
    getAllFunc()
  }

  const onJackpotInputChange = (gameId, field, value) => {
    const numericValue = `${value || ''}`.replace(/\D/g, '')
    setJackpotDraftByGame((prev) => ({
      ...prev,
      [gameId]: {
        ...(prev[gameId] || {}),
        [field]: numericValue,
      }
    }))
  }

  const formatJackpotValue = (value) => {
    if (!value) {
      return ''
    }
    const numericValue = Number(`${value}`.replace(/\D/g, ''))
    if (!numericValue) {
      return ''
    }
    return new Intl.NumberFormat('vi-VN').format(numericValue)
  }

  const onUpdateJackpotClick = async (row) => {
    if (!row.gameId) {
      return
    }

    try {
      setUpdatingJackpotGameId(row.gameId)
      const draft = jackpotDraftByGame[row.gameId] || {}
      await updateEstimateJackpotValue(
        cookies.lottmekong,
        row.gameId,
        draft.jackpot1 || 0,
        draft.jackpot2 || 0
      )
      await getAllFunc()
    } catch (error) {
      alert('Cập nhật jackpot không thành công')
    } finally {
      setUpdatingJackpotGameId('')
    }
  }

  const gameRows = useMemo(() => ([
    {
      key: 'lotto535',
      name: 'LOTO 5/35',
      single: data.isRollLotto535Enabled,
      group: data.isRollGroupLotto535Enabled,
      hasGroup: true,
    },
    {
      key: 'keno',
      name: 'Keno',
      single: data.isRollKenoEnabled,
      hasGroup: false,
    },
    {
      key: 'mega',
      name: 'Mega 6/45',
      single: data.isRollMegaEnabled,
      group: data.isRollGroupMegaEnabled,
      hasGroup: true,
    },
    {
      key: 'power',
      name: 'Power 6/55',
      single: data.isRollPowerEnabled,
      group: data.isRollGroupPowerEnabled,
      hasGroup: true,
    },
    {
      key: 'max3d',
      name: 'Max3D',
      single: data.isRollMax3dEnabled,
      hasGroup: false,
    },
    {
      key: 'max3dpro',
      name: 'Max3D Pro',
      single: data.isRollMax3dProEnabled,
      hasGroup: false,
    },
  ]), [data])

  const renderToggleCell = (isEnabled, onEnable, onDisable) => {
    if (typeof isEnabled !== 'boolean') {
      return <span className="text-muted">—</span>
    }

    return (
      <div className="d-flex flex-column align-items-center gap-2 py-2">
        <Badge bg={isEnabled ? 'success' : 'danger'}>
          {isEnabled ? 'Đang bật' : 'Đang tắt'}
        </Badge>
        <Button
          variant={isEnabled ? 'outline-danger' : 'outline-success'}
          size="sm"
          onClick={isEnabled ? onDisable : onEnable}
        >
          {isEnabled ? 'Tắt chia vé' : 'Bật chia vé'}
        </Button>
      </div>
    )
  }

  return (
    <>
      <Row className="g-3">
        {/* Row 1: Chia vé */}
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1">Điều khiển chia vé</h6>
                  <div className="small text-muted">Quản lý trạng thái chia vé theo từng trò chơi</div>
                </div>
                <Button variant="outline-primary" size="sm" onClick={getAllFunc}>Làm mới</Button>
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <Table responsive size="sm" className="align-items-center table-bordered table-hover mb-0">
                <thead className='text-center'>
                  <tr>
                    <th scope="col">Trò chơi</th>
                    <th scope="col">Chia vé</th>
                    <th scope="col">Chia vé mua chung</th>
                  </tr>
                </thead>
                <tbody>
                  {gameRows.map((row) => (
                    <tr key={row.key}>
                      <td className="text-center fw-bold">{row.name}</td>
                      <td className="text-center">
                        {renderToggleCell(
                          row.single,
                          () => onEnableRollClick(row.key),
                          () => onDisableRollClick(row.key)
                        )}
                      </td>
                      <td className="text-center">
                        {row.hasGroup
                          ? renderToggleCell(
                            row.group,
                            () => onEnableRollClick(row.key, true),
                            () => onDisableRollClick(row.key, true)
                          )
                          : <span className="text-muted">Không áp dụng</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Row 2: summary widgets */}
        <Col xs={12} md={6}>
          <Card border="light" className="shadow-sm h-100">
            <Card.Header>
              <h6 className="mb-1">Kỳ quay hiện tại</h6>
              <div className="small text-muted">Thông tin kỳ quay theo từng trò chơi</div>
            </Card.Header>
            <Card.Body className="pt-2">
              {periodRows.length === 0 ? (
                <div className="text-muted small">Chưa có dữ liệu kỳ quay hiện tại</div>
              ) : (
                <Table responsive size="sm" className="align-items-center table-bordered table-hover mb-0">
                  <thead className='text-center'>
                    <tr>
                      <th scope="col">Trò chơi</th>
                      <th scope="col">Kỳ hiện tại</th>
                      <th scope="col">JP1</th>
                      <th scope="col">JP2</th>
                      <th scope="col">Cập nhật</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodRows.map((row) => (
                      <tr key={row.key}>
                        <td className="text-center fw-bold text-uppercase">{row.label}</td>
                        <td className="text-center">{row.value}</td>
                        <td className="text-center">
                          {row.jackpot1 !== null ? (
                            <input
                              type="text"
                              inputMode="numeric"
                              className="form-control form-control-sm text-end"
                              value={formatJackpotValue(jackpotDraftByGame[row.gameId]?.jackpot1)}
                              onChange={(evt) => onJackpotInputChange(row.gameId, 'jackpot1', evt.target.value)}
                            />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          {row.jackpot2 !== null ? (
                            <input
                              type="text"
                              inputMode="numeric"
                              className="form-control form-control-sm text-end"
                              value={formatJackpotValue(jackpotDraftByGame[row.gameId]?.jackpot2)}
                              onChange={(evt) => onJackpotInputChange(row.gameId, 'jackpot2', evt.target.value)}
                            />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          {(row.jackpot1 !== null || row.jackpot2 !== null) ? (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={updatingJackpotGameId === row.gameId}
                              onClick={() => onUpdateJackpotClick(row)}
                            >
                              {updatingJackpotGameId === row.gameId ? 'Đang lưu...' : 'Lưu'}
                            </Button>
                          ) : (
                            <span className="text-muted">Không áp dụng</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <TicketSourceSummary embedded />
        </Col>
        <Col xs={12} md={6}>
          <MuaChungToggle compact />
        </Col>
        <Col xs={12}>
          <ModuleMaintainPanel moduleIds={['vietlott', 'keno', 'mega', 'power', 'max3d', 'max3dpro', 'lotto535', 'mua_chung']} />
        </Col>
      </Row>
    </>
  );
};
